import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { login, user, checking } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!checking && user) {
      navigate(user.role === "admin" ? "/flashcards/admin" : "/flashcards/dashboard", { replace: true });
    }
  }, [user, checking, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(identifier, password);
      navigate(u.role === "admin" ? "/flashcards/admin" : "/flashcards/dashboard", { replace: true });
    } catch (err) {
      setError(apiErr(err, "Incorrect username or password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-slate-900">
        <img src="/login-hero.webp"
          alt="Science Sorted" className="absolute inset-0 h-full w-full object-cover object-left-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-4xl font-bold leading-tight">Log in. See today's cards. Study. Done.</h2>
          <p className="mt-4 text-white/85 max-w-md">Your personal spaced-repetition schedule, ready whenever you are.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-sm ss-fade-up">
          <div className="lg:hidden mb-8 -mx-6 sm:-mx-12 -mt-6 sm:-mt-12">
            <div className="relative h-28 w-full">
              <img src="/login-hero.webp" alt="Science Sorted" className="absolute inset-0 h-full w-full object-cover object-left-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back</h1>
          <p className="mt-2 text-slate-500">Sign in to your flashcards.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="identifier">Username or email</Label>
              <Input id="identifier" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                required autoComplete="username" data-testid="login-email-input" className="mt-1.5 bg-white"
                placeholder="e.g. your first name" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required autoComplete="current-password" data-testid="login-password-input" className="mt-1.5 bg-white" />
            </div>
            {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full bg-[#FFC200] text-[#141821] hover:bg-[#141821] hover:text-[#FFC200] h-11 rounded-full font-semibold transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/flashcards/forgot-password" data-testid="forgot-password-link"
              className="text-sm text-slate-500 hover:text-slate-900">Forgot your password?</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
